/**
 * useDayEditModal - Custom hook que contiene todo el state, effects y handlers
 * del modal de edición de día. Extraído de DayEditModal.jsx para reducir su tamaño.
 */
import { useState, useEffect, useRef } from 'react';
import { motivos as motivosApi, lineas as lineasApi, user as userApi, recordatorios as recordatoriosApi, notas as notasApi, conductores as conductoresApi, bolsa as bolsaApi, horasSindicales } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';

// Helper to get day type from date string (YYYY-MM-DD)
const getDayType = (dateStr, festivos = []) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();

    if (festivos.includes(dateStr)) return 'D';
    if (dayOfWeek === 0) return 'D';
    if (dayOfWeek === 6) return 'S';
    return 'L-V';
};

export default function useDayEditModal({
    isOpen,
    onClose,
    dayData,
    conductorId,
    existingMarca,
    existingJornada,
    festivos = [],
    tourStep = null,
    onSave,
    onDelete,
    onNotesUpdated
}) {
    const { error: toastError, success: toastSuccess } = useToast();
    const { isAdmin } = useAuth();
    const { confirm } = useConfirm();

    // ===================== STATE =====================
    const [motivos, setMotivos] = useState([]);
    const [turnos, setTurnos] = useState([]);
    const [selectedMotivo, setSelectedMotivo] = useState(existingMarca?.motivo_id || null);
    const [selectedTurno, setSelectedTurno] = useState(existingMarca?.turno_id || null);
    const [comentario, setComentario] = useState(existingMarca?.comentario || '');
    const [noteText, setNoteText] = useState('');
    const [selectedTipo, setSelectedTipo] = useState('general');
    const [noteReminderEnabled, setNoteReminderEnabled] = useState(false);
    const [noteReminderHora, setNoteReminderHora] = useState('08:00');
    const [editingNotaId, setEditingNotaId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // CT Autocomplete state
    const [conductoresList, setConductoresList] = useState([]);
    const [ctPeerInput, setCtPeerInput] = useState('');
    const [ctPeerId, setCtPeerId] = useState(null);
    const [ctSearchResults, setCtSearchResults] = useState([]);
    const [showCtAutocomplete, setShowCtAutocomplete] = useState(false);

    // CF state
    const [cfMode, setCfMode] = useState(null);
    const [deudores, setDeudores] = useState([]);
    const [loadingDeudores, setLoadingDeudores] = useState(false);
    const [cfCompaneroId, setCfCompaneroId] = useState(null);
    const [cfCompaneroNombre, setCfCompaneroNombre] = useState('');

    // Notas state
    const [notas, setNotas] = useState(dayData?.notas || []);
    const [loadingNotas, setLoadingNotas] = useState(false);

    // Work registration state
    const [showWorkSection, setShowWorkSection] = useState(false);
    const [showTurnoSection, setShowTurnoSection] = useState(false);
    const [showMotivoSection, setShowMotivoSection] = useState(false);
    const [showComentarioSection, setShowComentarioSection] = useState(false);
    const [lineas, setLineas] = useState([]);
    const [servidores, setServidores] = useState([]);
    const [selectedLinea, setSelectedLinea] = useState(existingJornada?.linea_id || null);
    const [selectedServidor, setSelectedServidor] = useState(existingJornada?.servidor_id || null);
    const turnoJornada = dayData?.turno || null;
    const [numeroBus, setNumeroBus] = useState(existingJornada?.numero_bus || '');
    const [primasAplicables, setPrimasAplicables] = useState([]);
    const [loadingServidores, setLoadingServidores] = useState(false);

    // Reminder state
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [reminderHora, setReminderHora] = useState('08:00');
    const [reminderNotificado, setReminderNotificado] = useState(false);
    const initialReminderRef = useRef({ enabled: false, hora: '08:00' });

    // Extend period state
    const [extendEnabled, setExtendEnabled] = useState(false);
    const [extendEndDate, setExtendEndDate] = useState('');

    // Horas Sindicales state
    const [hsSaldo, setHsSaldo] = useState(null);

    // ===================== DERIVED STATE =====================
    const isDayRestDay = dayData?.isLibranza || dayData?.isVacacion;
    const currentTurnoLetter = dayData?.turno?.charAt?.(0)?.toUpperCase?.() || null;

    const WORK_DAY_TURNOS = ['Mañana', 'Tarde', 'CF'];
    const REST_DAY_TURNOS = ['Mañana', 'Tarde', 'CT'];
    const allowedTurnos = isDayRestDay ? REST_DAY_TURNOS : WORK_DAY_TURNOS;

    const filteredTurnos = (isAdmin() ? turnos : turnos.filter(t => allowedTurnos.includes(t.nombre)))
        .filter(t => {
            const turnoFirstLetter = t.nombre.charAt(0).toUpperCase();
            if (dayData?.turno === 'CT' || dayData?.turno === 'CF') return t.nombre !== dayData.turno;
            if (currentTurnoLetter === 'M' && turnoFirstLetter === 'M' && t.nombre === 'Mañana') return false;
            if (currentTurnoLetter === 'T' && turnoFirstLetter === 'T' && t.nombre === 'Tarde') return false;
            return true;
        });

    const getSelectedMotivoData = () => motivos.find(m => m.id === selectedMotivo);
    const isCurrentMotivoAusencia = getSelectedMotivoData()?.es_ausencia === 1 || getSelectedMotivoData()?.es_ausencia === true;
    const getExistingMotivoAusencia = () => {
        if (!existingMarca?.motivo_id || motivos.length === 0) return false;
        const m = motivos.find(mot => mot.id === existingMarca.motivo_id);
        return m?.es_ausencia === 1 || m?.es_ausencia === true;
    };
    const hasDayAusencia = isCurrentMotivoAusencia || getExistingMotivoAusencia();
    const isWorkDay = !dayData?.isLibranza && !dayData?.isVacacion;
    const hasActiveTurnoChange = existingMarca?.turno_id != null;

    // Validación de si se puede guardar (cambios detectados)
    const hasChanges = () => {
        const changedMotivo = selectedMotivo !== (existingMarca?.motivo_id || null);
        const changedTurno = selectedTurno !== (existingMarca?.turno_id || null);
        const changedComentario = comentario.trim() !== (existingMarca?.comentario || '').trim();
        const changedJornada = selectedLinea !== (existingJornada?.linea_id || null) || 
                               selectedServidor !== (existingJornada?.servidor_id || null) ||
                               numeroBus !== (existingJornada?.numero_bus || '');
        const hasNewNote = noteText.trim().length > 0;
        const changedReminder = reminderEnabled !== initialReminderRef.current.enabled ||
                                (reminderEnabled && reminderHora !== initialReminderRef.current.hora);
        
        return changedMotivo || changedTurno || changedComentario || changedJornada || hasNewNote || changedReminder || editingNotaId;
    };

    const canSave = hasChanges() && !saving;

    // ===================== EFFECTS =====================
    useEffect(() => {
        if (isOpen) {
            loadInitialData();
            setSelectedMotivo(existingMarca?.motivo_id || null);
            setSelectedTurno(existingMarca?.turno_id || null);
            setComentario(existingMarca?.comentario || '');
            setSelectedLinea(existingJornada?.linea_id || null);
            setSelectedServidor(existingJornada?.servidor_id || null);
            setNumeroBus(existingJornada?.numero_bus || '');
            setPrimasAplicables([]);
            setExtendEnabled(false);
            setExtendEndDate('');
            setNotas(dayData?.notas || []);
            setNoteText('');
            setSelectedTipo('general');
            setEditingNotaId(null);
            setNoteReminderEnabled(false);
            setNoteReminderHora('08:00');
            setCfMode(null);
            setDeudores([]);
            setCfCompaneroId(null);
            setCfCompaneroNombre('');
            loadNotas();
            loadReminder();
            loadHsSaldo();
        }
    }, [isOpen, existingMarca, existingJornada]);

    useEffect(() => {
        if (selectedLinea) {
            loadServidores(selectedLinea);
        } else {
            setServidores([]);
            setSelectedServidor(null);
            setPrimasAplicables([]);
        }
    }, [selectedLinea]);

    useEffect(() => {
        if (selectedServidor && turnoJornada) loadPrimas(selectedServidor, turnoJornada);
        else if (selectedServidor) loadPrimas(selectedServidor, null);
        else setPrimasAplicables([]);
    }, [selectedServidor, turnoJornada]);

    useEffect(() => {
        if (tourStep !== null) {
            setShowWorkSection(false);
            setShowTurnoSection(false);
            setShowMotivoSection(false);
            setShowComentarioSection(false);
            if (tourStep === 9) setShowWorkSection(true);
            if (tourStep === 10) setShowTurnoSection(true);
            if (tourStep === 11) setShowMotivoSection(true);
            if (tourStep === 12) setShowComentarioSection(true);
        }
    }, [tourStep]);

    useEffect(() => {
        const selectedTurnoObj = turnos.find(t => t.id === selectedTurno);
        if (selectedTurnoObj?.nombre === 'CF') loadDeudores();
    }, [selectedTurno, turnos]);

    // ===================== DATA LOADING =====================
    const loadInitialData = async () => {
        try {
            setLoading(true);
            const tipoDia = getDayType(dayData?.dateStr, festivos);
            const [motivosData, turnosData, lineasData, conductoresData] = await Promise.all([
                motivosApi.getAll(),
                motivosApi.getTurnos(),
                lineasApi.getAll(tipoDia),
                conductoresApi.getAll(true)
            ]);
            setMotivos(motivosData.filter(m => m.activo));
            setTurnos(turnosData);
            setLineas(lineasData);
            setConductoresList(conductoresData);
            if (existingJornada?.linea_id) loadServidores(existingJornada.linea_id);
        } catch (error) {
            console.error('Error loading initial data:', error);
            toastError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const loadServidores = async (lineaId) => {
        try {
            setLoadingServidores(true);
            const tDia = getDayType(dayData?.dateStr, festivos);
            const data = await lineasApi.getServidores(lineaId, tDia);
            setServidores(data);

            if (!selectedServidor && existingJornada?.servicio) {
                const searchCode = existingJornada.servicio.toUpperCase();
                let match = data.find(s => s.codigo?.toUpperCase() === searchCode);
                if (!match && (searchCode.startsWith('B') || searchCode.startsWith('L')) && searchCode.length > 1) {
                    const numericPart = searchCode.substring(1);
                    if (!isNaN(numericPart)) match = data.find(s => s.codigo === numericPart);
                }
                if (match) setSelectedServidor(match.id);
            }
        } catch (error) {
            console.error('Error loading servidores:', error);
            setServidores([]);
        } finally {
            setLoadingServidores(false);
        }
    };

    const loadPrimas = async (servidorId, turno = null) => {
        try {
            const data = await lineasApi.getServidorPrimas(servidorId, turno);
            setPrimasAplicables(data);
        } catch (error) {
            console.error('Error loading primas:', error);
            setPrimasAplicables([]);
        }
    };

    const loadNotas = async () => {
        if (!dayData?.dateStr || !conductorId) return;
        try {
            setLoadingNotas(true);
            const loadedNotas = await notasApi.getDia(conductorId, dayData.dateStr);
            setNotas(loadedNotas);
        } catch (error) {
            console.error('Error loading notas:', error);
        } finally {
            setLoadingNotas(false);
        }
    };

    const loadReminder = async () => {
        if (!dayData?.dateStr) return;
        try {
            const reminder = await recordatoriosApi.get(dayData.dateStr);
            if (reminder) {
                const hora = reminder.hora?.slice(0, 5) || '08:00';
                setReminderEnabled(true);
                setReminderHora(hora);
                setReminderNotificado(reminder.notificado || false);
                initialReminderRef.current = { enabled: true, hora };
            } else {
                setReminderEnabled(false);
                setReminderHora('08:00');
                setReminderNotificado(false);
                initialReminderRef.current = { enabled: false, hora: '08:00' };
            }
        } catch (error) {
            setReminderEnabled(false);
            setReminderHora('08:00');
            setReminderNotificado(false);
            initialReminderRef.current = { enabled: false, hora: '08:00' };
        }
    };

    const loadDeudores = async () => {
        try {
            setLoadingDeudores(true);
            const data = await bolsaApi.getDeudores();
            setDeudores(data);
        } catch (error) {
            console.error('Error loading deudores:', error);
            setDeudores([]);
        } finally {
            setLoadingDeudores(false);
        }
    };

    const loadHsSaldo = async () => {
        try {
            const year = dayData?.dateStr?.split('-')[0] || 2026;
            const data = await horasSindicales.getSaldo(year, conductorId);
            setHsSaldo(data);
        } catch (error) {
            console.error('Error loading HS saldo:', error);
        }
    };

    const handleSave = async () => {
        const hasMotivo = selectedMotivo || selectedTurno;
        const hasJornada = selectedLinea && selectedServidor;
        const hasComentarioMain = comentario.trim().length > 0;
        const hasNewNoteText = noteText.trim().length > 0;
        const hasReminder = reminderEnabled;
        const wantsToClearJornada = existingJornada && !selectedLinea;
        const isClearingExistingMarca = existingMarca && !selectedMotivo && !selectedTurno && !comentario.trim();

        if (!hasMotivo && !hasJornada && !hasComentarioMain && !hasNewNoteText && !hasReminder && !wantsToClearJornada && !isClearingExistingMarca && !editingNotaId) {
            toastError('Selecciona un turno/motivo, registra datos de jornada, añade una nota o activa un recordatorio');
            return;
        }

        try {
            setSaving(true);
            const isExtendingPeriod = extendEnabled && extendEndDate && extendEndDate >= dayData.dateStr;

            if (selectedMotivo && isExtendingPeriod) {
                const result = await motivosApi.marcarPeriodo({
                    conductor_id: conductorId,
                    fecha_inicio: dayData.dateStr,
                    fecha_fin: extendEndDate,
                    motivo_id: selectedMotivo,
                    comentario: comentario.trim() || null
                });
                toastSuccess(`${result.details?.total_dias || 'Varios'} días marcados con ${result.details?.motivo || 'el motivo'}`);
                if (onSave) onSave();
                onClose();
                return;
            }

            // Si el usuario ha vaciado todo y había algo antes, borramos el registro completo
            if (isClearingExistingMarca && !hasJornada && !hasReminder && !wantsToClearJornada) {
                await handleDelete();
                return;
            }

            if (hasMotivo || hasComentarioMain || (existingMarca && !selectedMotivo && !selectedTurno)) {
                const selectedTurnoObj = turnos.find(t => t.id === selectedTurno);
                const isCT = selectedTurnoObj && selectedTurnoObj.nombre === 'CT';
                const isCF = selectedTurnoObj && selectedTurnoObj.nombre === 'CF';

                await motivosApi.marcarDia({
                    conductor_id: conductorId,
                    fecha: dayData.dateStr,
                    motivo_id: selectedMotivo || null,
                    turno_id: selectedTurno || null,
                    comentario: comentario.trim() || null,
                    es_ct: isCT,
                    ct_companero_id: isCT ? ctPeerId : null,
                    ct_companero_nombre_manual: isCT && !ctPeerId ? ctPeerInput.trim() : null,
                    es_cf: isCF,
                    cf_companero_id: isCF ? (cfMode === 'cobrar' ? cfCompaneroId : ctPeerId) : null,
                    cf_companero_nombre_manual: isCF && cfMode === 'nuevo' && !ctPeerId ? ctPeerInput.trim() : null,
                    cf_es_cobro_deuda: isCF && cfMode === 'cobrar'
                });
            }

            if (hasJornada) {
                await userApi.saveJornada({
                    fecha: dayData.dateStr,
                    linea_id: selectedLinea,
                    servidor_id: selectedServidor,
                    turno_jornada: turnoJornada,
                    numero_bus: numeroBus || null
                });
            } else if (wantsToClearJornada) {
                await userApi.saveJornada({ fecha: dayData.dateStr, linea_id: null });
            }

            if (reminderEnabled) {
                await recordatoriosApi.save(dayData.dateStr, reminderHora, comentario.trim() || null);
            } else {
                try { await recordatoriosApi.delete(dayData.dateStr); } catch (e) { /* ignore */ }
            }

            toastSuccess(wantsToClearJornada && !hasJornada ? 'Jornada eliminada' : 'Día guardado correctamente');

            if (hasNewNoteText || editingNotaId) {
                try {
                    // Validar duplicidad básica si es nueva nota
                    if (!editingNotaId) {
                        const duplicate = notas.find(n => n.comentario.trim() === noteText.trim() && n.tipo === selectedTipo);
                        if (duplicate) {
                            toastError('Ya existe una nota idéntica para hoy');
                            setSaving(false);
                            return;
                        }
                    }

                    if (editingNotaId) {
                        await notasApi.update(editingNotaId, {
                            comentario: noteText.trim(),
                            tipo: selectedTipo,
                            reminder_time: noteReminderEnabled ? noteReminderHora : null,
                            reminder_notified: false
                        });
                    } else {
                        await notasApi.create({
                            conductor_id: conductorId,
                            fecha: dayData.dateStr,
                            comentario: noteText.trim(),
                            tipo: selectedTipo,
                            reminder_time: noteReminderEnabled ? noteReminderHora : null,
                            color: null
                        });
                    }
                    if (onNotesUpdated) await onNotesUpdated();
                    setNoteText('');
                    setSelectedTipo('general');
                    setEditingNotaId(null);
                    setNoteReminderEnabled(false);
                    await loadNotas();
                } catch (e) {
                    console.error('Error guardando nota:', e);
                    toastError('Error al guardar la nota');
                }
            }

            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error('Error saving:', error);
            toastError(error.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!existingMarca?.id) return;
        
        const ok = await confirm('¿Estás seguro de que quieres eliminar este motivo y volver al estado original del calendario?', {
            title: 'Eliminar Registro',
            confirmText: 'Sí, eliminar',
            type: 'danger'
        });

        if (!ok) return;

        try {
            setDeleting(true);
            await motivosApi.eliminarDia(existingMarca.id);
            if (onDelete) onDelete();
            if (onSave) onSave(); // Disparar onSave también para asegurar recarga completa en el padre
            onClose();
        } catch (error) {
            console.error('Error deleting:', error);
            toastError(error.message || 'Error al eliminar');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteTurnoOnly = async () => {
        const ok = await confirm('¿Quieres eliminar el cambio de turno de este día?', {
            title: 'Restablecer Turno',
            confirmText: 'Restablecer',
            type: 'warning'
        });

        if (!ok) return;

        try {
            setDeleting(true);
            if (!existingMarca?.motivo_id && !existingMarca?.comentario?.trim()) {
                await motivosApi.eliminarDia(existingMarca.id);
            } else {
                await motivosApi.marcarDia({
                    conductor_id: conductorId,
                    fecha: dayData.dateStr,
                    jornada_id: dayData.jornada?.id || existingJornada?.id,
                    motivo_id: existingMarca?.motivo_id || null,
                    turno_id: null,
                    comentario: existingMarca?.comentario || '',
                    es_ct: false,
                    es_cf: false
                });
            }
            if (onSave) onSave();
            onClose();
        } catch (error) {
            console.error('Error deleting turno:', error);
            toastError(error.message || 'Error al eliminar el turno');
        } finally {
            setDeleting(false);
        }
    };

    const handleEditNota = (nota) => {
        setEditingNotaId(nota.id);
        setNoteText(nota.comentario);
        setSelectedTipo(nota.tipo || 'general');
        if (nota.reminder_time) {
            setNoteReminderEnabled(true);
            setNoteReminderHora(nota.reminder_time.substring(0, 5));
        } else {
            setNoteReminderEnabled(false);
        }
    };

    const handleDeleteNota = async (notaId) => {
        const ok = await confirm('¿Quieres borrar esta nota permanentemente?', {
            title: 'Borrar Nota',
            confirmText: 'Eliminar'
        });

        if (!ok) return;

        try {
            await notasApi.delete(notaId);
            setNotas(notas.filter(n => n.id !== notaId));
            if (onNotesUpdated) await onNotesUpdated();
            toastSuccess('Nota eliminada');
        } catch (error) {
            console.error('Error eliminando nota:', error);
            toastError('Error al eliminar nota');
        }
    };

    // ===================== RETURN =====================
    return {
        // Loading / status
        loading, saving, deleting,

        // Derived
        filteredTurnos, hasDayAusencia, isWorkDay, hasActiveTurnoChange,

        // Motivos
        motivos, selectedMotivo, setSelectedMotivo,

        // Turnos
        turnos, selectedTurno, setSelectedTurno,

        // Comentario / Notas
        comentario, setComentario,
        noteText, setNoteText,
        canSave,
        selectedTipo, setSelectedTipo,
        noteReminderEnabled, setNoteReminderEnabled,
        noteReminderHora, setNoteReminderHora,
        editingNotaId, setEditingNotaId,
        notas, loadingNotas,

        // CT Autocomplete
        conductoresList,
        ctPeerInput, setCtPeerInput,
        ctPeerId, setCtPeerId,
        ctSearchResults, setCtSearchResults,
        showCtAutocomplete, setShowCtAutocomplete,

        // CF
        cfMode, setCfMode,
        deudores, loadingDeudores,
        cfCompaneroId, setCfCompaneroId,
        cfCompaneroNombre, setCfCompaneroNombre,

        // Work registration
        showWorkSection, setShowWorkSection,
        showTurnoSection, setShowTurnoSection,
        showMotivoSection, setShowMotivoSection,
        showComentarioSection, setShowComentarioSection,
        lineas, servidores, loadingServidores,
        selectedLinea, setSelectedLinea,
        selectedServidor, setSelectedServidor,
        turnoJornada,
        numeroBus, setNumeroBus,
        primasAplicables,

        // Reminder
        reminderEnabled, setReminderEnabled,
        reminderHora, setReminderHora,
        reminderNotificado, setReminderNotificado,

        // Extend period
        extendEnabled, setExtendEnabled,
        extendEndDate, setExtendEndDate,

        // Horas Sindicales
        hsSaldo,

        // Handlers
        handleSave,
        handleDelete,
        handleDeleteTurnoOnly,
        handleEditNota,
        handleDeleteNota
    };
}
